import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Progress } from './ui/progress';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Alert, AlertDescription } from './ui/alert';
import { 
  ArrowLeft, 
  ArrowRight, 
  CheckCircle, 
  Info, 
  Lightbulb,
  Download,
  MessageSquare,
  Building2,
  Users,
  Workflow,
  Lock,
  BarChart3,
  Settings,
  Plug,
  TrendingUp
} from 'lucide-react';
import { projectId } from '../utils/supabase/info';
import type { JSMSpaceAssessmentResponse, JSMSpaceAssessmentAnswer } from '../types';

// Terminology configuration - easy to change later
const TERMINOLOGY = {
  space: 'Space (Project)',
  spaces: 'Spaces (Projects)',
  SPACE: 'SPACE (PROJECT)',
  SPACES: 'SPACES (PROJECTS)'
};

interface JSMProjectDesignAssistantProps {
  companyId: string;
  companyName: string;
  accessToken: string;
  currentUser: any;
  isConsultant?: boolean;
  existingAssessment?: JSMSpaceAssessmentResponse;
  onComplete?: (assessment: JSMSpaceAssessmentResponse) => void;
  onSave?: (assessment: JSMSpaceAssessmentResponse) => void;
  mode?: 'client' | 'consultant-review';
}

// Assessment sections based on user's provided guide
const ASSESSMENT_SECTIONS = [
  {
    section: 1,
    title: 'Organization Structure',
    icon: Building2,
    description: 'How Your Organization Is Structured',
    thinkAbout: 'who provides services and how independently they operate',
    guidingInsight: 'Teams that operate independently usually benefit from their own project. Teams that share ownership or processes may prefer one unified project.',
    questions: [
      {
        id: 'org_departments',
        questionText: 'Which departments or teams will provide services through the portal?',
        inputType: 'checkbox' as const,
        options: [
          'IT Support',
          'HR',
          'Facilities',
          'Finance',
          'Legal',
          'Security',
          'Marketing',
          'Sales',
          'Customer Support',
          'Other'
        ],
        helpText: 'Select all that apply'
      },
      {
        id: 'org_independence',
        questionText: 'Do these teams share staff or operate separately?',
        inputType: 'radio' as const,
        options: [
          'Completely separate teams',
          'Some shared resources',
          'Mostly unified team'
        ]
      },
      {
        id: 'org_autonomy',
        questionText: 'Should each team manage its own SLAs, workflows, and reporting?',
        inputType: 'radio' as const,
        options: [
          'Yes, each team needs full autonomy',
          'Some autonomy but with consistency',
          'No, we want standardization across teams'
        ]
      }
    ]
  },
  {
    section: 2,
    title: 'Customer Experience',
    icon: Users,
    description: 'What the Customer Experience Should Feel Like',
    thinkAbout: 'how you want people to find and submit requests',
    guidingInsight: 'A single portal keeps things simple but can feel crowded. Separate portals (via multiple projects) can offer clarity and branding for distinct audiences.',
    questions: [
      {
        id: 'cx_users',
        questionText: 'Who will use the portal?',
        inputType: 'checkbox' as const,
        options: [
          'Internal employees only',
          'External clients',
          'Partners/vendors',
          'Multiple distinct customer groups'
        ]
      },
      {
        id: 'cx_portal_preference',
        questionText: 'Should customers see one combined catalog of services, or dedicated spaces?',
        inputType: 'radio' as const,
        options: [
          'One unified catalog for all services',
          'Separate portals for different departments',
          'Separate portals for different customer groups',
          'Mix of both'
        ]
      },
      {
        id: 'cx_branding',
        questionText: 'Would different audiences benefit from customized branding or categories?',
        inputType: 'radio' as const,
        options: [
          'Yes, each audience needs unique branding',
          'Some customization would be helpful',
          'No, unified branding is preferred'
        ]
      }
    ]
  },
  {
    section: 3,
    title: 'Process Variation',
    icon: Workflow,
    description: 'How Varied Your Processes Are',
    thinkAbout: 'how different your workflows, approvals, and automation rules are',
    guidingInsight: 'The more variation and independence your processes require, the more value you gain from separate projects.',
    questions: [
      {
        id: 'process_similarity',
        questionText: 'Do most requests follow the same basic steps (intake → triage → resolve)?',
        inputType: 'radio' as const,
        options: [
          'Yes, most requests are very similar',
          'Some variation but generally similar',
          'Significant variation across request types',
          'Each department has completely different processes'
        ]
      },
      {
        id: 'process_approvals',
        questionText: 'Are approval paths, forms, or SLAs different across departments?',
        inputType: 'radio' as const,
        options: [
          'Mostly the same',
          'Some differences',
          'Significantly different',
          'Completely different'
        ]
      },
      {
        id: 'process_evolution',
        questionText: 'Will teams want to evolve their processes independently in the future?',
        inputType: 'radio' as const,
        options: [
          'Yes, each team needs to iterate independently',
          'Some independence is valuable',
          'No, we want centralized control'
        ]
      }
    ]
  },
  {
    section: 4,
    title: 'Privacy & Visibility',
    icon: Lock,
    description: 'Who Should (and Should Not) See Requests',
    thinkAbout: 'privacy, sensitivity, and role visibility',
    guidingInsight: 'If visibility needs to be restricted by role or department, separate projects help enforce clean boundaries.',
    questions: [
      {
        id: 'privacy_confidential',
        questionText: 'Are there services involving confidential data (HR, Legal, Security, etc.)?',
        inputType: 'checkbox' as const,
        options: [
          'HR - employee records, complaints, personal matters',
          'Legal - contracts, disputes, compliance',
          'Security - incidents, investigations',
          'Finance - payroll, expenses, budgets',
          'Executive - board matters, strategic plans',
          'None - no confidential services'
        ]
      },
      {
        id: 'privacy_visibility',
        questionText: 'Should customers or agents in one area be able to view requests from another?',
        inputType: 'radio' as const,
        options: [
          'No, strict separation required',
          'Some cross-visibility is acceptable',
          'Yes, full transparency across all areas'
        ]
      },
      {
        id: 'privacy_compliance',
        questionText: 'Are there compliance or policy rules about data access?',
        inputType: 'radio' as const,
        options: [
          'Yes, strict compliance requirements (HIPAA, GDPR, etc.)',
          'Some compliance considerations',
          'No specific compliance requirements'
        ]
      }
    ]
  },
  {
    section: 5,
    title: 'Reporting & Metrics',
    icon: BarChart3,
    description: 'How You Want to Measure Success',
    thinkAbout: 'what insights you need and how you\'ll use them',
    guidingInsight: 'Unified reporting is easiest when everything lives in one project. Distinct reporting and ownership work better with separate projects.',
    questions: [
      {
        id: 'reporting_scope',
        questionText: 'Do you need combined reports across all services, or team-specific dashboards?',
        inputType: 'radio' as const,
        options: [
          'Combined reports across all services',
          'Team-specific dashboards primarily',
          'Both combined and team-specific',
          'Comparison metrics between teams'
        ]
      },
      {
        id: 'reporting_ownership',
        questionText: 'Who will review SLA performance?',
        inputType: 'checkbox' as const,
        options: [
          'Department managers (team-level)',
          'Executives (company-level)',
          'External auditors or compliance',
          'Team leads individually'
        ]
      },
      {
        id: 'reporting_comparison',
        questionText: 'Would comparing metrics between teams be helpful or confusing?',
        inputType: 'radio' as const,
        options: [
          'Very helpful - we want to benchmark',
          'Somewhat helpful',
          'Not needed',
          'Could be problematic or demotivating'
        ]
      }
    ]
  },
  {
    section: 6,
    title: 'Administration & Governance',
    icon: Settings,
    description: 'How Much Administration You Can Support',
    thinkAbout: 'who will maintain and govern the system',
    guidingInsight: 'More projects offer flexibility but require more administration. Fewer projects reduce maintenance but may limit customization.',
    questions: [
      {
        id: 'admin_ownership',
        questionText: 'Will one admin team manage configurations for everyone, or will each team handle its own?',
        inputType: 'radio' as const,
        options: [
          'Centralized admin team manages everything',
          'Each department has its own admin',
          'Mix - central for core, distributed for customization',
          'Not decided yet'
        ]
      },
      {
        id: 'admin_consistency',
        questionText: 'How important is consistency across request types, workflows, and SLAs?',
        inputType: 'radio' as const,
        options: [
          'Very important - standardization is key',
          'Somewhat important - balance needed',
          'Not very important - flexibility preferred',
          'Varies by department'
        ]
      },
      {
        id: 'admin_capacity',
        questionText: 'What is your team\'s capacity for ongoing administration?',
        inputType: 'radio' as const,
        options: [
          'Limited - need simplest possible structure',
          'Moderate - can handle some complexity',
          'Strong - dedicated admin resources available',
          'Unknown at this time'
        ]
      }
    ]
  },
  {
    section: 7,
    title: 'Integrations & External Access',
    icon: Plug,
    description: 'Integrations and External Access',
    thinkAbout: 'how other systems or partners will interact with JSM',
    guidingInsight: 'When external access or integration needs differ, separation can prevent overlap and confusion.',
    questions: [
      {
        id: 'integration_external',
        questionText: 'Will any external clients or vendors need private access to their own tickets?',
        inputType: 'radio' as const,
        options: [
          'Yes, multiple external parties need separate access',
          'Yes, one external party',
          'No external access needed',
          'Not sure yet'
        ]
      },
      {
        id: 'integration_tools',
        questionText: 'Do different service areas integrate with different tools?',
        inputType: 'checkbox' as const,
        options: [
          'Slack channels (different per team)',
          'Confluence spaces (different per team)',
          'Opsgenie/PagerDuty',
          'Email systems',
          'Custom APIs or third-party tools',
          'All teams use the same integrations'
        ],
        helpText: 'Examples: Slack, Confluence, Opsgenie'
      },
      {
        id: 'integration_automation',
        questionText: 'Should automations or notifications be managed differently by department?',
        inputType: 'radio' as const,
        options: [
          'Yes, each department needs unique automation',
          'Some customization needed',
          'No, standard automation for all'
        ]
      }
    ]
  },
  {
    section: 8,
    title: 'Future Growth',
    icon: TrendingUp,
    description: 'Your Future Growth and Scalability',
    thinkAbout: 'how your organization might evolve',
    guidingInsight: 'Design for tomorrow, not just today. A structure that\'s flexible and scalable will save time and rework later.',
    questions: [
      {
        id: 'growth_expansion',
        questionText: 'Are there new departments, regions, or clients you expect to add soon?',
        inputType: 'radio' as const,
        options: [
          'Yes, significant expansion planned (next 6-12 months)',
          'Some growth expected',
          'No major changes expected',
          'Unknown'
        ]
      },
      {
        id: 'growth_template',
        questionText: 'Would you like a repeatable project template for future expansion?',
        inputType: 'radio' as const,
        options: [
          'Yes, planning to replicate structure',
          'Maybe, want the option',
          'No, each expansion will be custom'
        ]
      },
      {
        id: 'growth_standards',
        questionText: 'Do you want to set naming standards or shared workflows now to prepare for growth?',
        inputType: 'radio' as const,
        options: [
          'Yes, want to standardize from the start',
          'Some standards would be helpful',
          'No, prefer flexibility now'
        ]
      }
    ]
  }
];

export function JSMProjectDesignAssistant({
  companyId,
  companyName,
  accessToken,
  currentUser,
  isConsultant = false,
  existingAssessment,
  onComplete,
  onSave,
  mode = 'client'
}: JSMProjectDesignAssistantProps) {
  const [currentSection, setCurrentSection] = useState(1);
  const [responses, setResponses] = useState<{ [key: string]: JSMSpaceAssessmentAnswer }>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showRecommendation, setShowRecommendation] = useState(false);
  const [recommendation, setRecommendation] = useState<any>(null);
  const [consultantNotes, setConsultantNotes] = useState('');
  const [otherValues, setOtherValues] = useState<{ [key: string]: string }>({});

  const isConsultantReview = mode === 'consultant-review';

  // Load existing assessment
  useEffect(() => {
    if (existingAssessment) {
      setResponses(existingAssessment.responses);
      if (existingAssessment.otherValues) {
        setOtherValues(existingAssessment.otherValues);
      }
      if (existingAssessment.status !== 'draft') {
        setShowRecommendation(true);
        setRecommendation({
          recommendedSpaceCount: existingAssessment.recommendedSpaceCount,
          recommendedStructure: existingAssessment.recommendedStructure,
          overallRecommendation: existingAssessment.overallRecommendation
        });
      }
      if (existingAssessment.consultantNotes) {
        setConsultantNotes(existingAssessment.consultantNotes);
      }
    } else {
      loadExistingAssessment();
    }
  }, [existingAssessment, companyId]);

  const loadExistingAssessment = async () => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/companies/${companyId}/jsm-assessment`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.assessment) {
          setResponses(data.assessment.responses || {});
          if (data.assessment.otherValues) {
            setOtherValues(data.assessment.otherValues);
          }
          if (data.assessment.status !== 'draft') {
            setShowRecommendation(true);
            setRecommendation({
              recommendedSpaceCount: data.assessment.recommendedSpaceCount,
              recommendedStructure: data.assessment.recommendedStructure,
              overallRecommendation: data.assessment.overallRecommendation
            });
          }
          if (data.assessment.consultantNotes) {
            setConsultantNotes(data.assessment.consultantNotes);
          }
        }
      }
    } catch (error) {
      console.error('Error loading JSM assessment:', error);
    }
  };

  const handleAnswerChange = (questionId: string, value: JSMSpaceAssessmentAnswer) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const calculateSectionScore = (section: number): number => {
    const sectionQuestions = ASSESSMENT_SECTIONS.find(s => s.section === section)?.questions || [];
    let score = 0;

    sectionQuestions.forEach(q => {
      const answer = responses[q.id];
      if (!answer) return;

      // Score based on answer patterns that indicate need for separation
      if (q.id === 'org_independence' && answer === 'Completely separate teams') score += 3;
      if (q.id === 'org_autonomy' && answer === 'Yes, each team needs full autonomy') score += 3;
      if (q.id === 'cx_portal_preference' && answer?.toString().includes('Separate')) score += 3;
      if (q.id === 'process_similarity' && answer?.toString().includes('different')) score += 3;
      if (q.id === 'process_evolution' && answer === 'Yes, each team needs to iterate independently') score += 2;
      if (q.id === 'privacy_confidential' && Array.isArray(answer) && answer.length > 1) score += 3;
      if (q.id === 'privacy_visibility' && answer === 'No, strict separation required') score += 3;
      if (q.id === 'privacy_compliance' && answer?.toString().includes('strict')) score += 2;
      if (q.id === 'reporting_scope' && answer?.toString().includes('team-specific')) score += 2;
      if (q.id === 'admin_ownership' && answer === 'Each department has its own admin') score += 2;
      if (q.id === 'integration_external' && answer?.toString().includes('multiple')) score += 2;
      if (q.id === 'integration_tools' && Array.isArray(answer) && answer.length > 2) score += 2;
      if (q.id === 'growth_template' && answer === 'Yes, planning to replicate structure') score += 2;
    });

    return Math.min(score, 10); // Cap at 10
  };

  const generateRecommendation = () => {
    const sectionScores = ASSESSMENT_SECTIONS.reduce((acc, section) => {
      acc[section.section] = calculateSectionScore(section.section);
      return acc;
    }, {} as { [key: number]: number });

    const totalScore = Object.values(sectionScores).reduce((sum, score) => sum + score, 0);
    const departments = responses['org_departments'] as string[] || [];
    const confidentialDepts = responses['privacy_confidential'] as string[] || [];

    let recommendedCount = 1;
    const structure: any[] = [];
    let reasoning = '';

    // Determine recommendation based on scores
    if (totalScore >= 60) {
      // High score = multiple projects likely needed
      recommendedCount = Math.max(2, Math.ceil(departments.length / 2));
      
      // Separate confidential departments
      const confidentialTeams = departments.filter(dept => 
        confidentialDepts.some(conf => conf.toLowerCase().includes(dept.toLowerCase()))
      );
      const regularTeams = departments.filter(dept => !confidentialTeams.includes(dept));

      if (confidentialTeams.length > 0) {
        structure.push({
          spaceName: 'Confidential Services',
          teams: confidentialTeams,
          reasoning: 'Requires privacy controls and restricted access'
        });
      }

      if (regularTeams.length > 0) {
        if (regularTeams.length <= 3) {
          structure.push({
            spaceName: 'General Services',
            teams: regularTeams,
            reasoning: 'Shared workflows and unified portal'
          });
        } else {
          // Split into 2 groups
          const mid = Math.ceil(regularTeams.length / 2);
          structure.push({
            spaceName: 'Operations Services',
            teams: regularTeams.slice(0, mid),
            reasoning: 'Similar operational processes'
          });
          structure.push({
            spaceName: 'Business Services',
            teams: regularTeams.slice(mid),
            reasoning: 'Business-focused services with distinct workflows'
          });
        }
      }

      reasoning = `Based on your responses, your organization shows strong indicators for multiple ${TERMINOLOGY.spaces.toLowerCase()}:
        
• Teams operate independently with distinct processes
• Privacy and compliance requirements need separation
• Different customer audiences benefit from dedicated portals
• Each area needs autonomy for workflows and SLAs

This structure balances clarity for customers with flexibility for your teams.`;

    } else if (totalScore >= 30) {
      // Medium score = hybrid approach
      recommendedCount = 2;
      
      const confidentialTeams = departments.filter(dept => 
        confidentialDepts.some(conf => conf.toLowerCase().includes(dept.toLowerCase()))
      );
      const regularTeams = departments.filter(dept => !confidentialTeams.includes(dept));

      if (confidentialTeams.length > 0) {
        structure.push({
          spaceName: 'Confidential Services',
          teams: confidentialTeams,
          reasoning: 'Separate for privacy and compliance'
        });
        structure.push({
          spaceName: 'General Services',
          teams: regularTeams,
          reasoning: 'Unified portal for non-confidential services'
        });
      } else {
        structure.push({
          spaceName: 'Unified Service Desk',
          teams: departments,
          reasoning: 'Consolidated approach with some team-level customization'
        });
        recommendedCount = 1;
      }

      reasoning = `Based on your responses, a hybrid approach works best:

• Some teams share processes while others need independence
• Privacy requirements suggest separating confidential services
• Unified reporting and administration are valuable
• Balance between simplicity and flexibility

This structure keeps administration manageable while respecting key boundaries.`;

    } else {
      // Low score = consolidated approach
      recommendedCount = 1;
      structure.push({
        spaceName: 'Unified Service Desk',
        teams: departments,
        reasoning: 'Shared workflows, unified portal, and centralized administration'
      });

      reasoning = `Based on your responses, a single ${TERMINOLOGY.space.toLowerCase()} is recommended:

• Teams share similar processes and workflows
• Unified customer experience is preferred
• Centralized administration and reporting
• Simpler to maintain and scale

This structure maximizes efficiency and keeps complexity low.`;
    }

    setRecommendation({
      recommendedSpaceCount: recommendedCount,
      recommendedStructure: structure,
      overallRecommendation: reasoning,
      sectionScores
    });
    setShowRecommendation(true);
  };

  const handleSaveAndContinue = async () => {
    if (currentSection < 8) {
      setCurrentSection(currentSection + 1);
      await saveProgress('draft');
    } else {
      generateRecommendation();
      await saveProgress('submitted');
    }
  };

  const handlePrevious = () => {
    if (currentSection > 1) {
      setCurrentSection(currentSection - 1);
    }
  };

  const saveProgress = async (status: 'draft' | 'submitted' | 'consultant-reviewing' | 'approved' = 'draft') => {
    setSaving(true);
    try {
      const assessmentData: Partial<JSMSpaceAssessmentResponse> = {
        companyId,
        responses,
        otherValues,
        status,
        lastModified: new Date().toISOString(),
        ...(status === 'submitted' && recommendation ? {
          recommendedSpaceCount: recommendation.recommendedSpaceCount,
          recommendedStructure: recommendation.recommendedStructure,
          overallRecommendation: recommendation.overallRecommendation,
          sectionScores: recommendation.sectionScores
        } : {})
      };

      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/companies/${companyId}/jsm-assessment`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify(assessmentData),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to save assessment');
      }

      const data = await response.json();
      
      if (onSave) {
        onSave(data.assessment);
      }

      if (status === 'submitted' && onComplete) {
        onComplete(data.assessment);
      }
    } catch (error) {
      console.error('Error saving JSM assessment:', error);
      alert('Failed to save assessment. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitForReview = async () => {
    await saveProgress('submitted');
  };

  const handleConsultantApprove = async () => {
    setSaving(true);
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/companies/${companyId}/jsm-assessment/approve`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            consultantNotes,
            status: 'approved'
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to approve assessment');
      }

      alert('Assessment approved. Client will be notified.');
      if (onComplete) {
        const data = await response.json();
        onComplete(data.assessment);
      }
    } catch (error) {
      console.error('Error approving assessment:', error);
      alert('Failed to approve assessment. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const currentSectionData = ASSESSMENT_SECTIONS.find(s => s.section === currentSection);
  const progress = ((currentSection - 1) / 8) * 100;
  const SectionIcon = currentSectionData?.icon || Info;

  const isCurrentSectionComplete = () => {
    const questions = currentSectionData?.questions || [];
    return questions.every(q => {
      const answer = responses[q.id];
      return answer !== undefined && answer !== null && answer !== '';
    });
  };

  if (showRecommendation) {
    return (
      <Card className="max-w-4xl mx-auto">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-8 w-8 text-green-600" />
              <div>
                <CardTitle>Your JSM {TERMINOLOGY.space} Recommendation</CardTitle>
                <CardDescription>Based on your assessment responses</CardDescription>
              </div>
            </div>
            <Badge variant={existingAssessment?.status === 'approved' ? 'default' : 'secondary'}>
              {existingAssessment?.status === 'approved' ? 'Approved' : 
               existingAssessment?.status === 'consultant-reviewing' ? 'In Review' :
               'Pending Review'}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <Alert>
            <Lightbulb className="h-4 w-4" />
            <AlertDescription>
              <div className="space-y-4">
                <div>
                  <h4 className="mb-2">We recommend: <strong>{recommendation?.recommendedSpaceCount} JSM {recommendation?.recommendedSpaceCount === 1 ? TERMINOLOGY.space : TERMINOLOGY.spaces}</strong></h4>
                </div>

                {recommendation?.recommendedStructure.map((space: any, index: number) => (
                  <div key={index} className="border-l-4 border-blue-500 pl-4 py-2 bg-blue-50 rounded">
                    <h5 className="mb-1">{TERMINOLOGY.space} {index + 1}: "{space.spaceName}"</h5>
                    <div className="text-sm mb-2">
                      <strong>Teams:</strong> {space.teams.join(', ')}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      <strong>Why:</strong> {space.reasoning}
                    </div>
                  </div>
                ))}

                <div className="mt-4 pt-4 border-t">
                  <h5 className="mb-2">Why this structure?</h5>
                  <p className="text-sm whitespace-pre-line">{recommendation?.overallRecommendation}</p>
                </div>
              </div>
            </AlertDescription>
          </Alert>

          {/* Display custom "Other" values if any */}
          {Object.keys(otherValues).length > 0 && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                <div className="space-y-2">
                  <h5 className="mb-2">Additional Details Provided:</h5>
                  {Object.entries(otherValues).map(([questionId, value]) => {
                    // Find the question to show context
                    const section = ASSESSMENT_SECTIONS.find(s => 
                      s.questions.some(q => q.id === questionId)
                    );
                    const question = section?.questions.find(q => q.id === questionId);
                    
                    return value ? (
                      <div key={questionId} className="text-sm">
                        <strong>{question?.questionText || 'Custom response'}:</strong>
                        <p className="ml-4 text-muted-foreground italic">"{value}"</p>
                      </div>
                    ) : null;
                  })}
                </div>
              </AlertDescription>
            </Alert>
          )}

          {isConsultantReview && (
            <div className="space-y-4 border-t pt-4">
              <Label htmlFor="consultant-notes">Consultant Notes</Label>
              <Textarea
                id="consultant-notes"
                placeholder="Add your notes, recommendations, or questions for the client..."
                value={consultantNotes}
                onChange={(e) => setConsultantNotes(e.target.value)}
                rows={4}
              />
              <div className="flex gap-2">
                <Button onClick={handleConsultantApprove} disabled={saving}>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Approve Assessment
                </Button>
                <Button variant="outline" disabled={saving}>
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Request Discussion
                </Button>
              </div>
            </div>
          )}

          {!isConsultantReview && existingAssessment?.consultantNotes && (
            <Alert>
              <MessageSquare className="h-4 w-4" />
              <AlertDescription>
                <strong>Consultant Notes:</strong>
                <p className="mt-2">{existingAssessment.consultantNotes}</p>
              </AlertDescription>
            </Alert>
          )}

          <div className="flex gap-2 pt-4">
            <Button variant="outline" onClick={() => setShowRecommendation(false)}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Review Answers
            </Button>
            <Button variant="outline">
              <Download className="h-4 w-4 mr-2" />
              Download Report
            </Button>
            {!isConsultantReview && existingAssessment?.status === 'approved' && onComplete && (
              <Button onClick={() => onComplete(existingAssessment)}>
                Continue to Configuration
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Progress Header */}
      <Card>
        <CardHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>JSM {TERMINOLOGY.space} Design Assistant</CardTitle>
                <CardDescription>
                  Question {currentSection} of 8 - Helping you determine the optimal structure
                </CardDescription>
              </div>
              <Badge variant="outline">
                {Math.round(progress)}% Complete
              </Badge>
            </div>
            <Progress value={progress} />
          </div>
        </CardHeader>
      </Card>

      {/* Current Section */}
      {currentSectionData && (
        <Card>
          <CardHeader>
            <div className="flex items-start gap-4">
              <div className="p-3 bg-primary/10 rounded-lg">
                <SectionIcon className="h-6 w-6 text-primary" />
              </div>
              <div className="flex-1">
                <CardTitle>{currentSectionData.title}</CardTitle>
                <CardDescription className="mt-2">
                  <strong>Think about:</strong> {currentSectionData.thinkAbout}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-8">
            {currentSectionData.questions.map((question) => (
              <div key={question.id} className="space-y-3">
                <Label className="text-base">{question.questionText}</Label>
                
                {question.inputType === 'checkbox' && (
                  <div className="space-y-2">
                    {question.options?.map((option) => (
                      <div key={option} className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id={`${question.id}-${option}`}
                            checked={(responses[question.id] as string[] || []).includes(option)}
                            onCheckedChange={(checked) => {
                              const current = (responses[question.id] as string[]) || [];
                              handleAnswerChange(
                                question.id,
                                checked
                                  ? [...current, option]
                                  : current.filter(v => v !== option)
                              );
                              // Clear the "Other" value if unchecked
                              if (!checked && option.toLowerCase() === 'other') {
                                setOtherValues(prev => {
                                  const updated = { ...prev };
                                  delete updated[question.id];
                                  return updated;
                                });
                              }
                            }}
                          />
                          <Label htmlFor={`${question.id}-${option}`} className="font-normal cursor-pointer">
                            {option}
                          </Label>
                        </div>
                        {/* Show text field when "Other" is checked */}
                        {option.toLowerCase() === 'other' && (responses[question.id] as string[] || []).includes(option) && (
                          <div className="ml-6 mt-2">
                            <Input
                              placeholder="Please specify..."
                              value={otherValues[question.id] || ''}
                              onChange={(e) => setOtherValues(prev => ({
                                ...prev,
                                [question.id]: e.target.value
                              }))}
                              className="max-w-md"
                            />
                          </div>
                        )}
                      </div>
                    ))}
                    {question.helpText && (
                      <p className="text-sm text-muted-foreground mt-2">{question.helpText}</p>
                    )}
                  </div>
                )}

                {question.inputType === 'radio' && (
                  <RadioGroup
                    value={responses[question.id] as string}
                    onValueChange={(value) => {
                      handleAnswerChange(question.id, value);
                      // Clear the "Other" value if switching away from "Other"
                      if (value.toLowerCase() !== 'other') {
                        setOtherValues(prev => {
                          const updated = { ...prev };
                          delete updated[question.id];
                          return updated;
                        });
                      }
                    }}
                  >
                    {question.options?.map((option) => (
                      <div key={option} className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value={option} id={`${question.id}-${option}`} />
                          <Label htmlFor={`${question.id}-${option}`} className="font-normal cursor-pointer">
                            {option}
                          </Label>
                        </div>
                        {/* Show text field when "Other" is selected */}
                        {option.toLowerCase() === 'other' && responses[question.id] === option && (
                          <div className="ml-6 mt-2">
                            <Input
                              placeholder="Please specify..."
                              value={otherValues[question.id] || ''}
                              onChange={(e) => setOtherValues(prev => ({
                                ...prev,
                                [question.id]: e.target.value
                              }))}
                              className="max-w-md"
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </RadioGroup>
                )}

                {question.inputType === 'textarea' && (
                  <Textarea
                    value={responses[question.id] as string || ''}
                    onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                    placeholder="Enter your response..."
                    rows={4}
                  />
                )}
              </div>
            ))}

            {/* Guiding Insight */}
            <Alert>
              <Lightbulb className="h-4 w-4" />
              <AlertDescription>
                <strong>Guiding insight:</strong> {currentSectionData.guidingInsight}
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      )}

      {/* Navigation */}
      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={handlePrevious}
          disabled={currentSection === 1 || saving}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Previous
        </Button>

        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => saveProgress('draft')}
            disabled={saving}
          >
            Save Progress
          </Button>
          <Button
            onClick={handleSaveAndContinue}
            disabled={!isCurrentSectionComplete() || saving}
          >
            {currentSection === 8 ? 'See Recommendation' : 'Save & Continue'}
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </div>

      {saving && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>Saving your progress...</AlertDescription>
        </Alert>
      )}
    </div>
  );
}